import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Typography,
  Space,
  Button,
  Tag,
  Divider,
  Alert,
  Tooltip,
  Flex,
  ModalProps,
  Row,
  Form,
  QRCode,
  Spin,
  QRCodeProps,
  Select,
  Input,
  Col,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MailOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { optionsContactIdd } from "../../constants/options";
import parsePhoneNumberFromString from "libphonenumber-js";
import { campaignService } from "../../services/campaignService";
import CustomModal from "./Modal";
import { CampaignRegisteredChannel } from "../../models/campaign";
import { browserLog } from "../../utils/log";

export type WhatsAppConfigModalProps = {
  title: string;
  open: boolean;
  onCancel: () => void;
  onContinue: (values: {
    clientId: string;
    email: string;
    contactIdd: string;
    contactNumber: string;
  }) => void;
  defaultEmail: string;
  defaultContactIdd?: string;
  defaultContactNumber?: string;
  registeredChannel?: CampaignRegisteredChannel[] | null;
};

const { Title, Text } = Typography;
const customStatusRender: QRCodeProps["statusRender"] = (info) => {
  switch (info.status) {
    case "expired":
      return (
        <div>
          <CloseCircleFilled style={{ color: "red" }} /> {info.locale?.expired}
          <p>
            <Button type="link" onClick={info.onRefresh}>
              <ReloadOutlined /> {info.locale?.refresh}
            </Button>
          </p>
        </div>
      );
    case "loading":
      return (
        <Space direction="vertical">
          <Spin size="large" />
          <p>Generating QR code...</p>
        </Space>
      );
    case "scanned":
      return (
        <div>
          <CheckCircleFilled style={{ color: "green" }} />{" "}
          {info.locale?.scanned}
        </div>
      );
    default:
      return null;
  }
};

const Scan: React.FC<WhatsAppConfigModalProps> = ({
  title,
  open,
  onCancel,
  onContinue,
  defaultEmail,
  defaultContactIdd,
  defaultContactNumber,
  registeredChannel,
}) => {
  const [form] = Form.useForm<{
    clientId: string;
    contactIddScan: string;
    contactNumberScan: string;
  }>();
  const [step, setStep] = useState<0 | 1>(0);
  const [active, setActive] = useState(false);
  const [valueQR, setValueQR] = useState("");
  const [loadingQR, setLoadingQR] = useState(false);
  const [checking, setChecking] = useState(false);
  const contactIdd = Form.useWatch("contactIddScan", form);
  const contactNumber = Form.useWatch("contactNumberScan", form);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [expiredQR, setExpiredQR] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(true);
  const pollingRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleBack = () => {
    clearTimers();
    if (title === "reactive") {
      setIsGenerating(false);
      onCancel();
    } else {
      setStep(0);
    }
  };

  const handleConnect = async () => {
    setChecking(true);
    try {
      checkActivate(true);
    } finally {
      setChecking(false);
    }
  };

  const clearTimers = () => {
    pollingRef.current = false;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const checkActivate = async (
    handleConnect: boolean = false,
    reactive: boolean = false,
    iddParam?: string,
    numberParam?: string,
  ) => {
    let idd = iddParam || contactIdd;
    let number = numberParam || contactNumber;
    if (reactive) {
      idd = iddParam || defaultContactIdd || "";
      number = numberParam || defaultContactNumber || "";
    }
    const data = await campaignService.checkActivate(idd + number);

    //CHANGE IF WANT BYPASS
    const bypass = false;
    if (data?.active === true || bypass) {
      setActive(true);
      clearTimers();
      browserLog("info", "data active ", data);

      onContinue({
        clientId: contactIdd + contactNumber,
        email: defaultEmail,
        contactIdd: contactIdd,
        contactNumber: contactNumber,
      });
    } else {
      if (handleConnect) {
        setIsErrorModalVisible(true);
      } else {
        setExpiredQR(true);
      }
    }
  };
  const handleContinue = async () => {
    setStep(1);
    generateQR(contactIdd, contactNumber);
  };
  const generateQR = async (iddParam?: string, numberParam?: string) => {
    let idd = iddParam || contactIdd;
    let number = numberParam || contactNumber;

    if (defaultEmail && idd && number) {
      try {
        setLoadingQR(true);

        const data1 = await campaignService.initClientId(idd + number);
        if (data1?.status === "error") {
          return;
        } else {
          checkActivate(false, false, idd, number);
        }
      } catch (error) {
        console.error("Error generate QR:", error);
      } finally {
        setLoadingQR(false);
      }
    }
  };

  const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
      timeoutRef.current = setTimeout(resolve, ms);
    });

  const generateQRCode = async (
    contactIddTarget: string,
    contactNumberTarget: string,
  ): Promise<void> => {
    if (isGenerating) return;
    if (!contactIddTarget || !contactNumberTarget) {
      return;
    }
    pollingRef.current = true;

    setIsGenerating(true);

    setExpiredQR(false);
    setLoadingQR(true);
    const tryGenerate = async (): Promise<void> => {
      if (!pollingRef.current) return;

      browserLog("info", "Generate QR called");

      const data = await campaignService.generateQR(
        contactIddTarget + contactNumberTarget,
      );
      browserLog("info", "Generate QR response", data);

      if (data?.status === "expired" || (data?.status === "no_qr" && active)) {
        browserLog("info", "expired or no qr with active", data);

        await new Promise((resolve) => setTimeout(resolve, 2000));

        return tryGenerate();
      } else {
        if (data != null && data?.status !== "no_qr") {
          const ttlMs = data.ttlMs ?? 2000;

          browserLog("info", "no_qr and active", data);

          setValueQR(data?.qr);
          setLoadingQR(false);

          timeoutRef.current = setTimeout(() => {
            if (!pollingRef.current) return;
            setExpiredQR(true);
          }, ttlMs);
        }
        setIsGenerating(false);

        await sleep(2000);

        return tryGenerate();
      }
    };

    await tryGenerate();
  };

  useEffect(() => {
    if (expiredQR && contactIdd && contactNumber && open) {
      generateQRCode(contactIdd, contactNumber);
    }
  }, [contactIdd, contactNumber, expiredQR, open]);

  useEffect(() => {
    if (title == "reactive") {
      setStep(1);
      if (defaultContactIdd != undefined && defaultContactNumber != undefined) {
        form.setFieldsValue({
          contactIddScan: defaultContactIdd,
          contactNumberScan: defaultContactNumber,
        });
        checkActivate(false, true, defaultContactIdd, defaultContactNumber);

        generateQR(defaultContactIdd, defaultContactNumber);
      }
    }
  }, [defaultContactIdd, defaultContactNumber, title]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        width={680}
        title={
          <Title level={4} style={{ margin: 0 }}>
            {title === "reactive"
              ? "Reactive WhatsApp"
              : "Add New Mobile Number"}
          </Title>
        }
      >
        <Title level={4} style={{ textAlign: "center", marginBottom: 4 }}>
          {step === 0
            ? title === "reactive"
              ? "Reactive WhatsApp"
              : "Add New Mobile Number"
            : "Scan QR Code"}
        </Title>
        <Text
          type="secondary"
          style={{ display: "block", textAlign: "center", marginBottom: 16 }}
        >
          {step === 0
            ? "Provide your WhatsApp-registered number to get started"
            : "Scan this QR code to verify your new mobile number"}
        </Text>
        <Alert
          className="warning-configuration-whatsapp"
          type="warning"
          message={<strong>Prerequisite</strong>}
          description={
            <span>
              Integration requires a verified WhatsApp Business account.{" "}
              <a
                href="https://business.whatsapp.com/products/business-app-get-started"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here
              </a>{" "}
              to verify or set up your account.
            </span>
          }
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form
          form={form}
          requiredMark={false}
          layout="vertical"
          style={{ marginTop: 12 }}
          preserve={true}
        >
          <div style={{ display: step === 0 ? "block" : "none" }}>
            <>
              <Form.Item
                label="Mobile Number"
                required
                extra={
                  <Text type="secondary">
                    This must be the WhatsApp-registered mobile number. You’ll
                    scan a QR code next to connect and enable campaign sending.
                  </Text>
                }
                style={{ marginBottom: 5 }}
              >
                <Space.Compact className="w-100">
                  <Form.Item
                    name="contactIddScan"
                    noStyle
                    initialValue="65"
                    rules={[{ required: true, message: "Select country code" }]}
                  >
                    <Select
                      style={{ width: 150 }}
                      options={optionsContactIdd}
                      placeholder="Code"
                      onChange={() => {
                        form.validateFields(["contactNumberScan"]);
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="contactNumberScan"
                    noStyle
                    rules={[
                      { required: true, message: "Enter phone number" },
                      {
                        validator: async (_, value) => {
                          const code = form.getFieldValue("contactIddScan");
                          if (!code || !value) {
                            return Promise.resolve();
                          }

                          if (!/^\d+$/.test(value)) {
                            return Promise.reject(
                              new Error("Please enter a valid mobile number"),
                            );
                          }
                          const phone = parsePhoneNumberFromString(
                            `+${code}${value}`,
                          );
                          if (!phone || !phone.isValid()) {
                            setError(true);
                            return Promise.reject(
                              new Error("Please enter a valid mobile number"),
                            );
                          }
                          // check is already exist
                          const exists = registeredChannel?.some(
                            (x) =>
                              x.Status === true &&
                              x.ContactNumber === phone.nationalNumber &&
                              x.ContactIdd === code,
                          );

                          if (exists) {
                            setError(true);
                            return Promise.reject(
                              new Error(
                                "This number is already registered for this channel",
                              ),
                            );
                          }
                          setError(false);

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input placeholder="8112334567" style={{ width: "100%" }} />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
              <Alert
                type="info"
                description={
                  <span>
                    WhatsApp sessions are time-limited. Once a session expires,
                    you will receive an SMS and email notification prompting you
                    to reactivate the connection by scanning a new QR code.
                  </span>
                }
                showIcon
                style={{ marginBottom: 16 }}
              />
            </>
          </div>

          <div style={{ display: step === 1 ? "block" : "none" }}>
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "1px dashed #d9d9d9",
                  borderRadius: 12,
                  minHeight: 240,
                  marginBottom: 8,
                }}
              >
                {!active ? (
                  <QRCode
                    value={valueQR || "TEST"}
                    size={200}
                    // onRefresh={() =>
                    //   generateQRCode(emailValue.replace(/[\W_]+/g, ""))
                    // }
                    statusRender={customStatusRender}
                    status={loadingQR ? "loading" : "active"}
                  />
                ) : (
                  <QRCode
                    value={"success"}
                    size={200}
                    statusRender={customStatusRender}
                    status={"scanned"}
                  />
                )}
              </div>

              <Row justify="center" style={{ marginBottom: 4 }}>
                <Text type="secondary">
                  Once scanned, your WhatsApp will be activated and connected to
                  this platform.
                </Text>
              </Row>

              <Form.Item name="clientId" style={{ display: "none" }}>
                <Input />
              </Form.Item>
            </>
          </div>
        </Form>
        <Row gutter={[12, 12]}>
          <Col span={12}>
            {step === 0 ? (
              <Button onClick={onCancel} className="w-100" size="large">
                Cancel
              </Button>
            ) : (
              <Button onClick={handleBack} className="w-100" size="large">
                Back
              </Button>
            )}
          </Col>
          <Col span={12}>
            {" "}
            {step === 0 ? (
              <Button
                size="large"
                type="primary"
                className="w-100"
                disabled={error}
                onClick={handleContinue}
              >
                Continue
              </Button>
            ) : (
              <Button
                size="large"
                className="w-100"
                type="primary"
                onClick={handleConnect}
                loading={checking}
              >
                I’ve Scanned QR Code
              </Button>
            )}
          </Col>
        </Row>
      </Modal>
      <CustomModal
        type="error"
        title="Activation Failed"
        message="We couldn’t verify your WhatsApp account. Please try scanning the QR code again."
        isVisible={isErrorModalVisible}
        onClose={() => setIsErrorModalVisible(false)}
        successButtonText="Retry"
        onSuccessClick={() => handleConnect()}
        cancelButtonText="Go Back"
        onCancelClick={() => setIsErrorModalVisible(false)}
      />
    </>
  );
};

export default Scan;
